import { useEffect, useRef, useState } from "react";
import { apiRequest } from '@/lib/api';
import Modal from "@/components/base/Modal";
import { getDataClient } from "@/lib/dataClient";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { createPayment } from "@/hooks/usePayments";

interface PurchasePackage {
  id: string;
  name: string;
  price: number;
  billingCycle?:"monthly"|"yearly";
}

interface PurchaseModalProps {
  open: boolean;
  onClose: () => void;
  pkg: PurchasePackage | null;
  onPurchased: () => void;
}

function formatPrice(value: number): string {
  return `₺${value.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PurchaseModal({ open, onClose, pkg, onPurchased }: PurchaseModalProps) {
  const { settings } = useSiteSettingsContext();
  const [method, setMethod] = useState<"paytr" | "havale" | null>(null);
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [havaleDone, setHavaleDone] = useState(false);
  const [quote,setQuote]=useState<{amount:number;billing_cycle:string;discount_percent:number}|null>(null);
  const paymentRequest=useRef(crypto.randomUUID());
  const packageId=pkg?.id;
  const billingCycle=pkg?.billingCycle||"monthly";
  useEffect(()=>{
    let current=true;setQuote(null);setError(null);paymentRequest.current=crypto.randomUUID();
    if(open&&packageId)apiRequest<{quote:{amount:number;billing_cycle:string;discount_percent:number}}>('/billing/quote',{method:'POST',body:{package_id:packageId,billing_cycle:billingCycle}})
      .then(data=>{if(current)setQuote(data.quote);}).catch(e=>{if(current)setError(e.message);});
    return()=>{current=false;};
  },[open,packageId,billingCycle]);

  const reset = () => {
    setMethod(null);
    setIframeUrl("");
    setLoading(false);
    setError(null);
    setHavaleDone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const startPaytr = async () => {
    if (!pkg || !quote) return;
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();
      const { data, error: fnErr } = await dataClient.functions.invoke("paytr-create-token", {
        body: { package_id: pkg.id, billing_cycle:pkg.billingCycle||'monthly', expected_amount_cents:Math.round(quote.amount*100) },
      });
      if (fnErr || !data?.ok || !data?.iframeUrl) {
        setError(fnErr?.message || data?.error || "PayTR başlatılamadı.");
        return;
      }
      setIframeUrl(data.iframeUrl);
    } catch {
      setError("Ödeme başlatılırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const confirmHavale = async () => {
    if (!pkg || !quote) return;
    setLoading(true);
    setError(null);
    try {
      await createPayment(pkg.id, pkg.name, quote.amount, "havale",pkg.billingCycle||'monthly',paymentRequest.current);
      setHavaleDone(true);
      onPurchased();
    } catch (e) {
      setError(e instanceof Error?e.message:"Ödeme kaydı oluşturulurken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const hasBankInfo = !!(settings.bankName || settings.bankIban);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={iframeUrl ? "Ödeme" : `Paket Satın Al — ${pkg?.name ?? ""}`}
      titleId="purchase-modal-title"
      id="purchase-modal"
      maxWidthClass={iframeUrl ? "max-w-3xl" : "max-w-md"}
    >
      {!pkg ? null : iframeUrl ? (
        <div className="space-y-3">
          <p className="text-sm text-foreground-600">
            Ödemeyi aşağıdaki güvenli form üzerinden tamamla. İşlem bitince otomatik olarak yönlendirileceksin.
          </p>
          <div className="w-full h-[480px] rounded-lg overflow-hidden border border-background-200">
            <iframe
              src={iframeUrl}
              title="PayTR güvenli ödeme"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      ) : method === null ? (
        <div className="space-y-3">
          <p className="text-sm text-foreground-600">
            <span className="font-semibold text-foreground-900">{pkg.name}</span> paketi için ödeme yöntemini seç:
          </p>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          {!quote && !error && <p role="status">Tutar doğrulanıyor...</p>}
          <p className="text-sm">{pkg.billingCycle === "yearly" ? "12 aylık erişim · yıllık peşin ödeme" : "1 aylık erişim"}</p>
          <div className="text-right">
            <span className="text-2xl font-bold text-foreground-950">{formatPrice(quote?.amount ?? pkg.price)}</span>
          </div>
          <div className="grid gap-2.5">
            <button
              type="button"
              onClick={() => setMethod("paytr")}
              disabled={!settings.paytrEnabled || !quote}
              className="h-12 rounded-md border border-background-200 bg-background-50 hover:border-primary-300 transition-colors flex items-center justify-between px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex items-center gap-2.5 text-sm font-semibold text-foreground-900">
                <i className="ri-bank-card-line text-primary-600 text-lg" aria-hidden="true"></i>
                Kredi Kartı ile Öde
              </span>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            </button>
            {!settings.paytrEnabled && (
              <p className="text-xs text-foreground-400">Kredi kartı ödemesi şu an kapalı.</p>
            )}
            <button
              type="button"
              onClick={() => setMethod("havale")}
              disabled={!quote}
              className="h-12 rounded-md border border-background-200 bg-background-50 hover:border-primary-300 transition-colors flex items-center justify-between px-4"
            >
              <span className="flex items-center gap-2.5 text-sm font-semibold text-foreground-900">
                <i className="ri-bank-line text-accent-600 text-lg" aria-hidden="true"></i>
                Havale / EFT
              </span>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      ) : method === "paytr" ? (
        <div className="space-y-4">
          <div className="rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-700">
            <span className="font-semibold">{pkg.name}</span> — {formatPrice(quote?.amount ?? pkg.price)}
          </div>
          {error && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setMethod(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Geri
            </button>
            <button
              type="button"
              onClick={startPaytr}
              disabled={loading || !quote}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {loading ? "Başlatılıyor..." : "Ödemeye Geç"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {havaleDone ? (
            <div className="rounded-lg bg-accent-100/70 px-4 py-6 text-center">
              <i className="ri-checkbox-circle-line text-3xl text-accent-700" aria-hidden="true"></i>
              <p className="mt-2 text-sm font-semibold text-foreground-900">Ödeme talebin alındı</p>
              <p className="mt-1 text-sm text-foreground-600">
                Havale onaylandığında paketin otomatik olarak aktifleşecek.
              </p>
            </div>
          ) : hasBankInfo ? (
            <>
              <div className="rounded-lg border border-background-200 bg-background-100 p-4 space-y-2.5">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide">Havale Bilgileri</p>
                {settings.bankName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground-600">Banka</span>
                    <span className="text-sm font-semibold text-foreground-900">{settings.bankName}</span>
                  </div>
                )}
                {settings.bankAccountName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground-600">Hesap Sahibi</span>
                    <span className="text-sm font-semibold text-foreground-900">{settings.bankAccountName}</span>
                  </div>
                )}
                {settings.bankIban && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-foreground-600">IBAN</span>
                    <span className="text-sm font-mono font-semibold text-foreground-900 break-all">{settings.bankIban}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-600">Tutar</span>
                  <span className="text-sm font-semibold text-foreground-900">{formatPrice(quote?.amount ?? pkg.price)}</span>
                </div>
              </div>
              {error && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setMethod(null)}
                  className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Geri
                </button>
                <button
                  type="button"
                  onClick={confirmHavale}
                  disabled={loading || !quote}
                  className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  {loading ? "Kaydediliyor..." : "Havaleyi Yaptım"}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-foreground-600 bg-background-100 rounded-md px-4 py-3">
                Havale bilgileri henüz tanımlanmamış. Lütfen destek ekibiyle iletişime geç.
              </p>
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setMethod(null)}
                  className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Geri
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
